import type { ApiErrorBody } from "../contract/types";

export type ApiErrorCode = ApiErrorBody["error"]["code"] | "transport_error";

/** Erreur normalisée renvoyée par toute la couche API (contrat §9). */
export class ApiError extends Error {
  readonly code: ApiErrorCode;
  readonly httpStatus: number | null;
  readonly details: Record<string, unknown> | undefined;

  constructor(
    code: ApiErrorCode,
    message: string,
    opts: { httpStatus?: number | null; details?: Record<string, unknown>; cause?: unknown } = {},
  ) {
    super(message, opts.cause !== undefined ? { cause: opts.cause } : undefined);
    this.name = "ApiError";
    this.code = code;
    this.httpStatus = opts.httpStatus ?? null;
    this.details = opts.details;
  }

  /** L'écran doit-il proposer un simple réessai ? (panne transport / 5xx) */
  get isRetryable(): boolean {
    return this.code === "transport_error" || (this.httpStatus !== null && this.httpStatus >= 500);
  }
}

function isApiErrorBody(value: unknown): value is ApiErrorBody {
  if (typeof value !== "object" || value === null) return false;
  const err = (value as { error?: unknown }).error;
  return (
    typeof err === "object" &&
    err !== null &&
    typeof (err as { code?: unknown }).code === "string" &&
    typeof (err as { message?: unknown }).message === "string"
  );
}

/** Construit une ApiError à partir d'une réponse HTTP non 2xx. */
export async function apiErrorFromResponse(response: Response): Promise<ApiError> {
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    body = undefined;
  }

  if (isApiErrorBody(body)) {
    return new ApiError(body.error.code, body.error.message, {
      httpStatus: response.status,
      ...(body.error.details !== undefined ? { details: body.error.details } : {}),
    });
  }

  const fallback: ApiErrorCode =
    response.status === 401 || response.status === 403
      ? "unauthenticated"
      : response.status === 404
        ? "not_found"
        : response.status === 409
          ? "conflict"
          : "transport_error";
  return new ApiError(fallback, `HTTP ${response.status} ${response.statusText}`.trim(), {
    httpStatus: response.status,
  });
}
